import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import UserBadge from '../components/UserBadge';
import RouteMap from '../components/RouteMap';
import LoadingSpinner from '../components/LoadingSpinner';
import SensorMeasurements from '../components/SensorMeasurements';
import FlightDiagrams from '../components/FlightDiagrams';
import SkewTChart from '../components/SkewTChart';
import LikeButton from '../components/LikeButton';
import FlightDangerZone from '../components/FlightDangerZone';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest, UPLOADS_BASE_URL } from '../config/api';
import { Aircraft, Coordinate, MeasurementResult, formatDate, formatDuration, getAircraftTypeName, calculateDistance, getMaxAltitude, handleFlightExport } from '../utils/flightUtils';
import { getAvatarStyle, getInitials } from '../utils/avatarUtils';
import androidLogo from '../assets/android-logo.svg';
import appleLogo from '../assets/apple-logo.svg';

interface Pilot {
  name: string;
  organization?: string;
  logo?: string;
}

interface PublicFlightTrack {
  _id: string;
  userid: string;
  aircraftid: Aircraft;
  dateStart: string;
  dateEnd: string;
  duration: number;
  coordinates?: Coordinate[];
  region?: string;
  notes?: string;
  pilot: Pilot | null;
  deviceInfo?: string | null;
}

const PublicFlightDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, userDetails, isStaff } = useAuth();
  const [flight, setFlight] = useState<PublicFlightTrack | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCount, setLikeCount] = useState(0);
  const [likedByMe, setLikedByMe] = useState(false);
  const [measurements, setMeasurements] = useState<MeasurementResult[] | null>(null);
  const [hoverTimestamp, setHoverTimestamp] = useState<number | null>(null);
  const [scrollToTimestamp, setScrollToTimestamp] = useState<{ time: number; trigger: number } | null>(null);

  const handleChartClick = React.useCallback((timestamp: number) => {
    setScrollToTimestamp({ time: timestamp, trigger: Date.now() });
  }, []);

  useEffect(() => {
    if (!id) return;
    const fetchFlight = async () => {
      try {
        const response = await apiRequest(`/flight-tracks/public/${id}?includeCoordinates=true`);
        if (!response.ok) throw new Error('Flug nicht gefunden');
        const data = await response.json();
        setFlight(data);
        const likeRes = await apiRequest(`/likes/flight/${data._id}`);
        if (likeRes.ok) {
          const likeData = await likeRes.json();
          setLikeCount(likeData.count);
          setLikedByMe(likeData.likedByMe);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };
    fetchFlight();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const fetchMeasurements = async () => {
      try {
        const res = await apiRequest(`/sensor-data/${id}`);
        if (!res.ok) { setMeasurements([]); return; }
        const data = await res.json();
        setMeasurements(data.measurements || []);
      } catch {
        setMeasurements([]);
      }
    };
    fetchMeasurements();
  }, [id]);

  const handleExport = async (format: 'csv' | 'pdf') => {
    if (!flight) return;
    try {
      await handleFlightExport(
        id!,
        flight.aircraftid.identification,
        flight.dateStart,
        '/flight-tracks/public',
        format,
        {
          dateEnd: flight.dateEnd,
          coordinates: flight.coordinates,
          measurements: measurements ?? undefined,
        }
      );
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <LoadingSpinner message="Lade Flugdaten..." fullPage />
      </div>
    );
  }

  if (error || !flight) {
    return (
      <div className="container" style={{ padding: '40px' }}>
        <div className="alert alert-danger">{error || 'Flug nicht gefunden'}</div>
        <BackButton to="/all-flights" label="Zurück zu Alle Flüge" />
      </div>
    );
  }

  const distance = calculateDistance(flight.coordinates || []);
  const maxAltitude = getMaxAltitude(flight.coordinates || []);
  const canDelete = isStaff || (userDetails?.userid === flight.userid || user?.sub === flight.userid);

  return (
    <div className="container" style={{ padding: '40px' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <BackButton to="/all-flights" label="Alle Flüge" />
        <UserBadge />
      </div>

      {/* Top Info Section */}
      <div className="row g-4 mb-4">
        {/* Flight info */}
        <div className="col-xxl-4 col-lg-6 col-12">
          <div className="card shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4 d-flex flex-column justify-content-between">
              <div>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h5 style={{ fontWeight: 700, color: '#687088', margin: 0 }}>
                    {flight.aircraftid.identification}
                  </h5>
                  <LikeButton targetType="flight" targetId={flight._id} initialCount={likeCount} initialLiked={likedByMe} />
                </div>
                <p style={{ color: '#687088', fontSize: '14px', marginBottom: '4px' }}>
                  {getAircraftTypeName(flight.aircraftid.type)} · {flight.region || 'Unbekannte Region'} · {formatDate(flight.dateStart)}
                </p>
                <p style={{ color: '#687088', fontSize: '14px', marginBottom: 0 }}>
                  Dauer {formatDuration(flight.duration)} · Distanz {distance.toFixed(1)} km · Max Höhe {maxAltitude.toFixed(0)} m
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Pilot */}
        <div className="col-xxl-4 col-lg-6 col-12">
          <div className="card shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Pilot</h5>
              {flight.pilot ? (
                <div className="d-flex align-items-center gap-3">
                  {flight.pilot.logo ? (
                    <img
                      src={`${UPLOADS_BASE_URL}${flight.pilot.logo}`}
                      alt={flight.pilot.name}
                      style={{ width: '52px', height: '52px', borderRadius: '6px', objectFit: 'contain', border: '1px solid #e0e4ef' }}
                    />
                  ) : (
                    <div style={getAvatarStyle(flight.pilot.name, 52)}>
                      {getInitials(flight.pilot.name)}
                    </div>
                  )}
                  <div>
                    <div
                      style={{ fontWeight: 700, color: '#0d6efd', fontSize: '16px', cursor: 'pointer' }}
                      onClick={() => navigate(`/pilots/${flight.userid}`)}
                    >
                      {flight.pilot.name}
                    </div>
                    {flight.pilot.organization && (
                      <div style={{ color: '#a0afc0', fontSize: '14px' }}>
                        {flight.pilot.organization}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p style={{ color: '#a0afc0', fontSize: '14px', margin: 0 }}>Keine Pilotinformationen verfügbar.</p>
              )}
            </div>
          </div>
        </div>

        {/* Geräteinformationen */}
        <div className="col-xxl-4 col-lg-6 col-12">
          <div className="card shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Geräteinformationen</h5>
              {flight.deviceInfo ? (() => {
                const parts = flight.deviceInfo.split(';').map((s) => s.trim());
                const os = parts[0] || flight.deviceInfo;
                const model = parts.slice(1).join(' • ') || 'Unbekannt';
                const isAndroid = os.toLowerCase().startsWith('android');
                const isIos = os.toLowerCase().startsWith('ios');

                return (
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <span style={{ fontSize: '12px', color: '#687088', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                        Betriebssystem
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        {isAndroid && <img src={androidLogo} alt="Android" style={{ height: '18px', width: 'auto' }} />}
                        {isIos && <img src={appleLogo} alt="Apple" style={{ height: '18px', width: 'auto' }} />}
                        <span style={{ color: '#202124', fontSize: '14px' }}>{os}</span>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <span style={{ fontSize: '12px', color: '#687088', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                        Gerätemodell
                      </span>
                      <span style={{ color: '#202124', fontSize: '14px' }}>{model}</span>
                    </div>
                  </div>
                );
              })() : (
                <p style={{ color: '#687088', fontSize: '14px', margin: 0 }}>
                  Unbekanntes Gerät
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notes (read-only) */}
      {flight.notes && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-body p-4">
                <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Notizen</h5>
                <p style={{ color: '#687088', fontSize: '14px', margin: 0, whiteSpace: 'pre-wrap' }}>
                  {flight.notes}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Route Map - Full Width */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Route</h5>
              <div style={{ position: 'relative' }}>
                <RouteMap
                  coordinates={flight.coordinates || []}
                  aircraftType={flight.aircraftid.type}
                  height="500px"
                  measurements={measurements ?? undefined}
                  showSensorOverlay={true}
                  hoverTimestamp={hoverTimestamp}
                />
                <div style={{
                  position: 'absolute', top: '16px', right: '16px', background: 'white',
                  padding: '8px 12px', borderRadius: '8px', fontWeight: 700, color: '#0d6efd',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 1000,
                }}>
                  {distance.toFixed(2)} km
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Diagramme - Full Width */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Diagramme</h5>
              <FlightDiagrams
                flightId={flight._id}
                coordinates={flight.coordinates}
                dateStart={flight.dateStart}
                dateEnd={flight.dateEnd}
                hoverTimestamp={hoverTimestamp}
                onHoverTimestamp={setHoverTimestamp}
                onClickTimestamp={handleChartClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Temp-Diagramm (Skew-T log-P) - Full Width */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Temp-Diagramm (Skew-T log-P)</h5>
              <SkewTChart
                flightId={flight._id}
                measurements={measurements}
                coordinates={flight.coordinates}
                dateStart={flight.dateStart}
                dateEnd={flight.dateEnd}
                hoverTimestamp={hoverTimestamp}
                onHoverTimestamp={setHoverTimestamp}
                onClickTimestamp={handleChartClick}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Export - Full Width */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '12px' }}>Export</h5>
              <div className="d-flex gap-2">
                <button className="btn btn-primary" style={{ fontWeight: 700, fontSize: '13px', width: '200px' }} onClick={() => handleExport('pdf')}>
                  Export PDF
                </button>
                <button className="btn btn-outline-primary" style={{ fontWeight: 700, fontSize: '13px', width: '200px' }} onClick={() => handleExport('csv')}>
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messungen */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm mb-4" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Messungen</h5>
              <SensorMeasurements
                flightTrackId={id!}
                coordinates={flight.coordinates}
                dateStart={flight.dateStart}
                dateEnd={flight.dateEnd}
                scrollToTimestamp={scrollToTimestamp}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Gefahrenbereich: Flug löschen (nur Eigentümer oder Staff) */}
      {canDelete && (
        <FlightDangerZone
          flightId={flight._id}
          aircraftIdentification={flight.aircraftid.identification}
          dateStart={flight.dateStart}
          redirectTo="/all-flights"
        />
      )}
    </div>
  );
};

export default PublicFlightDetail;
